import React, {PureComponent} from "react";
import withStyles from '@mui/styles/withStyles';
import {PageDataContext} from "../../../core/PageDataContext";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import {AttributeValuesPrettier} from "../../../core/prettifier/AttributeValuesPrettier";
import IconButton from "@mui/material/IconButton";
import MessageIcon from "@mui/icons-material/Message";
import moment from "moment";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import LineGraph from "../../graphs/LineGraph";
import {WorkoutVolumeParser} from "../../../core/parser/WorkoutVolumeParser";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import Popover from "@mui/material/Popover";
import SortIcon from "@mui/icons-material/Sort";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DonutSmallIcon from "@mui/icons-material/DonutSmall";
import {ColorPicker} from "../../../core/colors/ColorPicker";
import {Doughnut} from "react-chartjs-2";
import {ExerciseType} from "../../../core/models/ExerciseType";
import {ExerciseTypeAttribute} from "../../../core/models/ExerciseTypeAttribute";
import Button from "@mui/material/Button";
import PersonalRecordBadge from "./PersonalRecordBadge";
import WorkoutComparisonTab from "./WorkoutComparisonTab";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";

const useStyles = theme => ({
	logComment: {
		padding: theme.spacing(2),
	},
});

class WorkoutLogsSection extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			workout: props.workout,
			isGraphsLoading: false,
			dateRangeFetched: '3_months',
			routineWorkouts: props.cachedWorkoutsData ?? {},
			graphsMenuAnchorEl: null,
			logComment: null,
			dropDownCommentAnchorEl: null,
			isComparisonOpen: true,
			isMusclesOpen: false,
		};
	}

	componentDidMount() {
		if (this.props.shouldUpdate === undefined || this.props.shouldUpdate) {
			this.fetchWorkoutData();
		}
	}

	componentWillReceiveProps(nextProps, nextContext) {
		if (!nextProps.shouldUpdate || this.state.workout === nextProps.workout) {
			return;
		}

		this.setState({
			workout: nextProps.workout,
		});

		this.fetchWorkoutData(null, nextProps.workout);
	}

	componentWillUnmount() {
		if ('saveCachedWorkoutsData' in this.props) {
			this.props.saveCachedWorkoutsData(this.state.routineWorkouts);
		}
	}

	toggleComparison = () => {
		this.setState(prev => ({isComparisonOpen: !prev.isComparisonOpen}));
	}

	toggleMuscles = () => {
		this.setState(prev => ({isMusclesOpen: !prev.isMusclesOpen}));
	}

	openGraphMenu = (event) => {
		this.setState({
			graphsMenuAnchorEl: event.currentTarget
		});
	}

	closeGraphMenu = () => {
		this.setState({
			graphsMenuAnchorEl: null
		});
	}

	openDropDownCommentAnchorEl = (event, comment) => {
		event.stopPropagation();
		this.setState({
			dropDownCommentAnchorEl: event.currentTarget,
			logComment: comment,
		});
	};

	closeDropDownCommentAnchorEl = () => {
		this.setState({
			dropDownCommentAnchorEl: null,
			logComment: null,
		});
	};

	fetchWorkoutData = async(start = null, workout = null) => {
		workout = workout ?? this.state.workout;

		if (workout?.routines.length === 0) {
			return;
		}

		let end = workout.dateTimeStarted;

		let datetime = (moment(end)).subtract(3, 'months');
		let dateRangeFetched = '3_months';
		if (start) {
			switch (start) {
				case '1_month':
					datetime = (moment()).subtract(1, 'month');
					dateRangeFetched = '1_month';
					break;
				case '3_months':
					datetime = (moment()).subtract(3, 'months');
					dateRangeFetched = '3_months';
					break;
				case '6_months':
					datetime = (moment()).subtract(6, 'months');
					dateRangeFetched = '6_months';
					break;
				case '1_year':
					datetime = (moment()).subtract(1, 'year');
					dateRangeFetched = '1_year';
					break;
				case 'all_time':
					datetime = null;
					dateRangeFetched = 'all_time';
			}
		}

		this.setState({
			isGraphsLoading: true,
			dateRangeFetched: dateRangeFetched,
			graphsMenuAnchorEl: null,
		});

		let existingRoutineWorkouts = {};
		for (const routine of workout.routines) {
			const fetchUrl = this.props.sharedToken
				? UrlBuilder.workout.sharedRoutineWorkoutsApi(
					this.props.sharedToken,
					routine.id,
					datetime ? datetime.format('L') : null,
					workout.dateTimeStarted
				)
				: UrlBuilder.workout.workoutsForRoutineApi(
					routine.id,
					datetime ? datetime.format('L') : null,
					workout.dateTimeStarted
				);
			const routineWorkoutsResponse = await fetch(fetchUrl).then(res => res.json());

			const routineWorkouts = routineWorkoutsResponse.data;

			existingRoutineWorkouts[routine.id] = {
				name: routine.name,
				workouts: routineWorkouts,
			};

			this.setState({
				routineWorkouts: existingRoutineWorkouts,
			});
			this.forceUpdate();
		}

		this.setState({
			isGraphsLoading: false,
		});
	}

	renderVolumeGraphs = () => {
		const dateLabels = {
			'1_month': '1 month',
			'3_months': '3 months',
			'6_months': '6 months',
			'1_year': '1 year',
			'all_time': 'All time',
		};

		return (
			<Box sx={{ mt: 1 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						Volume Over Time
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						{this.state.isGraphsLoading && (
							<CircularProgress color="primary" size={16} />
						)}
						<Chip
							label={dateLabels[this.state.dateRangeFetched] || '3 months'}
							size="small"
							variant="outlined"
							onClick={this.openGraphMenu}
							onDelete={this.openGraphMenu}
							deleteIcon={<SortIcon sx={{ fontSize: 16 }} />}
							sx={{ fontWeight: 500, fontSize: 11 }}
						/>
					</Box>
				</Box>

				<Menu
					anchorEl={this.state.graphsMenuAnchorEl}
					open={Boolean(this.state.graphsMenuAnchorEl)}
					onClose={this.closeGraphMenu}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				>
					{Object.entries(dateLabels).map(([key, label]) => (
						<MenuItem
							key={key}
							dense
							onClick={() => this.fetchWorkoutData(key)}
							selected={this.state.dateRangeFetched === key}
						>
							<Typography variant="body2">{label}</Typography>
						</MenuItem>
					))}
				</Menu>

				{Object.keys(this.state.routineWorkouts).map((routineId) =>
					<Box key={routineId} sx={{ mb: 2 }}>
						<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
							{this.state.routineWorkouts[routineId]?.name}
						</Typography>
						<LineGraph data={WorkoutVolumeParser.parse(this.state.routineWorkouts[routineId]?.workouts)}/>
					</Box>
				)}
			</Box>
		)
	}

	renderMuscleBreakdown = () => {
		let musclesWorkByExerciseType = {};

		this.state.workout?.routines.forEach(routine => {
			routine.exercises.forEach(exercise => {
				exercise.muscles.forEach(exerciseMuscle => {
					let totalWorkForExercise = 0;

					for (const log of exercise.logs) {
						switch (exercise.type.id) {
							case ExerciseType.WeightAndReps:
							case ExerciseType.DistanceAndTime:
							case ExerciseType.WeightAndTime:
								totalWorkForExercise += log.multiplied * exerciseMuscle.percentage / 100;
								break;
							case ExerciseType.Reps:
							case ExerciseType.Time:
								totalWorkForExercise += log.values[0] * exerciseMuscle.percentage / 100;
								break;
						}
					}

					musclesWorkByExerciseType[exercise.type.name] = musclesWorkByExerciseType[exercise.type.name] ?? {};

					musclesWorkByExerciseType[exercise.type.name][exerciseMuscle.muscleId] = {
						name: exerciseMuscle.muscle.name,
						workTotal: totalWorkForExercise +
							(
								musclesWorkByExerciseType[exercise.type.name] && musclesWorkByExerciseType[exercise.type.name][exerciseMuscle.muscleId]
									? musclesWorkByExerciseType[exercise.type.name][exerciseMuscle.muscleId].workTotal
									: 0
							),
					};
				})
			})
		});

		if (Object.keys(musclesWorkByExerciseType).length === 0) {
			return <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 1 }}>
				No muscles configured for these exercises.
			</Typography>
		}

		let volumeChartDataByExerciseType = {};

		Object.keys(musclesWorkByExerciseType).forEach(exerciseTypeName => {
			let labels = [];
			let backgroundColors = [];
			let totalWorkPerMuscle = [];

			let muscles = musclesWorkByExerciseType[exerciseTypeName];
			Object.keys(muscles).forEach(muscleId => {
				let muscle = muscles[muscleId];

				labels.push(muscle.name);
				backgroundColors.push(ColorPicker.pick(muscle.name + '_ab'));

				totalWorkPerMuscle.push(muscle.workTotal);
			});

			let totalWork = totalWorkPerMuscle.reduce((a,b) => a + b, 0);

			volumeChartDataByExerciseType[exerciseTypeName] =  {
				labels: labels,
				datasets: [
					{
						label: 'Work per Muscle',
						data: totalWorkPerMuscle.map(muscleWork => Math.round(muscleWork / totalWork * 1000) / 10),
						backgroundColor: backgroundColors,
						borderWidth: 0,
						borderColor: '#aeaeae'
					},
				],
			};
		});

		let exerciseTypes = Object.keys(volumeChartDataByExerciseType);

		return <>
			{exerciseTypes.map(exerciseTypeName =>
				<React.Fragment key={exerciseTypeName}>
					{exerciseTypes.length > 1 &&
						<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
							{exerciseTypeName}-based exercises
						</Typography>
					}

					<Doughnut
						data={volumeChartDataByExerciseType[exerciseTypeName]}
						options={{
							legend: {
								position: 'bottom',
							},
						}}
					/>
				</React.Fragment>
			)}
		</>;
	}

	getLogPrType = (logId) => {
		const prs = this.props.personalRecords;
		if (!prs || prs.length === 0) return null;
		const pr = prs.find(p => p.logId === logId);
		return pr ? pr.prType : null;
	}

	render() {
		const {classes} = this.props;

		if (this.state.workout.routines.length === 0) {
			return (
				<Box sx={{ textAlign: 'center', py: 4 }}>
					<Typography variant="body2" sx={{ color: 'text.secondary' }}>
						No exercises logged in this workout yet.
					</Typography>
				</Box>
			);
		}

		return <>
			{this.state.logComment && this.state.dropDownCommentAnchorEl && (
				<Popover
					open={this.state.dropDownCommentAnchorEl !== null}
					anchorEl={this.state.dropDownCommentAnchorEl}
					onClose={this.closeDropDownCommentAnchorEl}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
					transformOrigin={{ vertical: 'top', horizontal: 'center' }}
				>
					<Typography variant='body2' className={classes.logComment}>
						{this.state.logComment}
					</Typography>
				</Popover>
			)}

			{this.state.workout.routines.map(routine =>
				<Box key={routine.id} sx={{ mb: 2.5 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
						{routine.name}
					</Typography>

					{routine.exercises.map(exercise => {
						const key = routine.id + '_' + exercise.id;

						return (
							<Box key={key} sx={{ mb: 1.5 }}>
								{/* Exercise header */}
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
									<Avatar sx={{
										width: 28,
										height: 28,
										fontSize: 12,
										fontWeight: 600,
										bgcolor: exercise.color ?? ColorPicker.pick(exercise.name),
									}}>
										{exercise.name.charAt(0).toUpperCase()}
									</Avatar>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{exercise.name}
									</Typography>
									<Typography variant="caption" sx={{ color: 'text.disabled' }}>
										{exercise.logs.length} set{exercise.logs.length !== 1 ? 's' : ''}
									</Typography>
								</Box>

								{/* Sets */}
								<Box sx={{ pl: 5.5 }}>
									{exercise.logs.map((log, idx) => (
										<Box
											key={log.id}
											sx={{
												display: 'flex',
												alignItems: 'center',
												py: 0.4,
												borderBottom: idx < exercise.logs.length - 1 ? 1 : 0,
												borderColor: 'divider',
											}}
										>
											<Typography variant="caption" sx={{
												color: 'text.disabled',
												fontWeight: 600,
												width: 20,
												flexShrink: 0,
											}}>
												{idx + 1}
											</Typography>
											<Typography variant="body2" sx={{ flex: 1 }}>
												{AttributeValuesPrettier.prettifyAttributeValues(exercise.type, log)}
												{exercise.type.attributes.find(attr => attr.key === ExerciseTypeAttribute.Reps) && !isNaN(log.rpeValue) && !isNaN(log.rirValue) && (
													<Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
														@{log.rpeValue},{log.rirValue}
													</Typography>
												)}
												{this.getLogPrType(log.id) && (
													<PersonalRecordBadge prType={this.getLogPrType(log.id)} />
												)}
											</Typography>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												{log.comment && (
													<IconButton size="small" onClick={(e) => this.openDropDownCommentAnchorEl(e, log.comment)} sx={{ p: 0.25 }}>
														<MessageIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
													</IconButton>
												)}
												<Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
													{moment(log.dateCreated).format('HH:mm')}
												</Typography>
											</Box>
										</Box>
									))}
								</Box>
							</Box>
						);
					})}
				</Box>
			)}

			<Divider sx={{ my: 2 }} />

			{/* vs Previous - collapsible card */}
			<Card variant="outlined" sx={{ borderRadius: 2.5, mb: 1.5 }}>
				<CardActionArea onClick={this.toggleComparison}>
					<CardHeader
						sx={{ py: 1.25 }}
						avatar={<CompareArrowsIcon sx={{ color: 'text.secondary', fontSize: 22 }} />}
						title={
							<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
								vs Previous Workout
							</Typography>
						}
						action={
							<IconButton size="small">
								{this.state.isComparisonOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
							</IconButton>
						}
					/>
				</CardActionArea>
				<Collapse in={this.state.isComparisonOpen}>
					<CardContent sx={{ pt: 0 }}>
						<WorkoutComparisonTab workoutId={this.state.workout.id} />
					</CardContent>
				</Collapse>
			</Card>

			{/* Muscles - collapsible card */}
			<Card variant="outlined" sx={{ borderRadius: 2.5, mb: 1.5 }}>
				<CardActionArea onClick={this.toggleMuscles}>
					<CardHeader
						sx={{ py: 1.25 }}
						avatar={<DonutSmallIcon sx={{ color: 'text.secondary', fontSize: 22 }} />}
						title={
							<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
								Muscle Breakdown
							</Typography>
						}
						action={
							<IconButton size="small">
								{this.state.isMusclesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
							</IconButton>
						}
					/>
				</CardActionArea>
				<Collapse in={this.state.isMusclesOpen}>
					<CardContent sx={{ pt: 0 }}>
						{this.renderMuscleBreakdown()}
					</CardContent>
				</Collapse>
			</Card>

			{/* Volume graphs */}
			{this.renderVolumeGraphs()}
		</>;
	}
}

WorkoutLogsSection.contextType = PageDataContext;

export default withStyles(useStyles)(WorkoutLogsSection);
